import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  dismissLoader,
  showLoader,
  updateScreenTitle,
} from "../../../LayoutContainers/AppLayout/redux/AppSlice";
import AppService from "../../../Configurations/Appservice";
import {
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { FORMAT_DATE } from "../../../Configurations/JsUtil";
import {
  Edit as EditIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";

const CustomerPaymentMethods = (props) => {
  const [methods, setMethods] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [methodToDelete, setMethodToDelete] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(updateScreenTitle("Payment Methods"));
    return () => dispatch(updateScreenTitle(""));
  }, []);

  const loadMethods = () => {
    dispatch(showLoader());
    AppService.get("v1/customerPaymentMethod/myPaymentMethods")
      .then((response) => {
        setMethods(response);
      })
      .catch((error) => {})
      .finally(() => {
        dispatch(dismissLoader());
      });
  };

  useEffect(() => {
    loadMethods();
  }, []);

  const handleView = (method) => {
    setSelectedMethod(method);
    setOpenViewDialog(true);
  };

  const handleEdit = (method) => {
    // Navigate to edit page with method ID
    const methodId = method?.id || method?._id || method?.code || method?.paymentMethodId;
    console.log("Edit button clicked", { method, methodId });
    if (methodId) {
      console.log(`Navigating to /wallet/edit_customer_payment_method/${methodId}`);
      navigate(`/wallet/edit_customer_payment_method/${methodId}`);
    } else {
      console.error("No method ID found", method);
    }
  };

  const handleDeleteClick = (method) => {
    setMethodToDelete(method);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (methodToDelete?.id) {
      dispatch(showLoader());
      AppService.delete(`v1/customerPaymentMethod/${methodToDelete.id}`)
        .then(() => {
          setOpenDeleteDialog(false);
          setMethodToDelete(null);
          loadMethods();
        })
        .catch((error) => {
          console.error("Failed to delete payment method", error);
        })
        .finally(() => {
          dispatch(dismissLoader());
        });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="flex-end" mb={3}>
        <Button
          variant="contained"
          sx={{ bgcolor: '#1a8a7e', color: '#fff', '&:hover': { bgcolor: '#147a6e' } }}
          onClick={() => navigate("/wallet/new_customer_payment_method")}
        >
          Add Payment Method
        </Button>
      </Box>
      {methods?.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No payment methods found.
        </Typography>
      ) : (
        <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#1a8a7e' }}>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Source</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Description</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Created Date</TableCell>
                <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {methods.map((m) => (
                <TableRow
                  key={
                    m?.id || m?._id || m?.code || m?.paymentMethodId || `${m?.name || ''}-${m?.paymentMethod?.name || ''}`
                  }
                >
                  <TableCell>{m?.name || '-'}</TableCell>
                  <TableCell>{m?.paymentMethod?.name || '-'}</TableCell>
                  <TableCell>{m?.comment || '-'}</TableCell>
                  <TableCell>{FORMAT_DATE(m?.createDateTime)}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="View">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleView(m)}
                      >
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        color="info"
                        onClick={() => handleEdit(m)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteClick(m)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* View Dialog */}
      <Dialog
        open={openViewDialog}
        onClose={() => setOpenViewDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            backgroundImage: 'none',
          }
        }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #1a5f6b 0%, #2a8fa3 100%)',
          color: 'white',
          fontWeight: 'bold',
          py: 3
        }}>
          Payment Method Details
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 1 }}>
          {selectedMethod && (
            <Box>
              {/* Name Field */}
              <Box sx={{
                mb: 1.5,
                p: 1,
                bgcolor: '#f5f5f5',
                borderRadius: 1,
                borderLeft: '4px solid #1a5f6b'
              }}>
                <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                  Name
                </Typography>
                <Typography variant="h6" sx={{ mt: 0.2, fontWeight: 500, fontSize: '0.95rem' }}>
                  {selectedMethod?.name || '-'}
                </Typography>
              </Box>

              {/* Payment Method Type */}
              <Box sx={{
                mb: 1.5,
                p: 1,
                bgcolor: '#f5f5f5',
                borderRadius: 1,
                borderLeft: '4px solid #2a8fa3'
              }}>
                <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                  Payment Method Type
                </Typography>
                <Typography variant="h6" sx={{ mt: 0.2, fontWeight: 500, fontSize: '0.95rem' }}>
                  {selectedMethod?.paymentMethod?.name || selectedMethod?.paymentMethodType || '-'}
                </Typography>
              </Box>

              {/* Bank Details Grid */}
              {(selectedMethod?.bankName || selectedMethod?.accountNumber || selectedMethod?.iban || selectedMethod?.branch) && (
                <Box sx={{ mb: 1.5, p: 1, bgcolor: '#fafafa', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 600, color: '#1a5f6b', fontSize: '0.85rem' }}>
                    Bank Details
                  </Typography>
                  {selectedMethod?.bankName && (
                    <Box sx={{ mb: 0.75 }}>
                      <Typography variant="caption" sx={{ color: '#999', fontWeight: 600, fontSize: '0.65rem' }}>Bank Name</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{selectedMethod.bankName}</Typography>
                    </Box>
                  )}
                  {selectedMethod?.accountNumber && (
                    <Box sx={{ mb: 0.75 }}>
                      <Typography variant="caption" sx={{ color: '#999', fontWeight: 600, fontSize: '0.65rem' }}>Account Number</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>****{selectedMethod.accountNumber.slice(-4)}</Typography>
                    </Box>
                  )}
                  {selectedMethod?.iban && (
                    <Box sx={{ mb: 0.75 }}>
                      <Typography variant="caption" sx={{ color: '#999', fontWeight: 600, fontSize: '0.65rem' }}>IBAN</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{selectedMethod.iban}</Typography>
                    </Box>
                  )}
                  {selectedMethod?.branch && (
                    <Box sx={{ mb: 0 }}>
                      <Typography variant="caption" sx={{ color: '#999', fontWeight: 600, fontSize: '0.65rem' }}>Branch</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{selectedMethod.branch}</Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* Description */}
              {selectedMethod?.comment && (
                <Box sx={{
                  mb: 1.5,
                  p: 1,
                  bgcolor: '#fffbf0',
                  borderRadius: 1,
                  borderLeft: '4px solid #ff9800'
                }}>
                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                    Description
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.2, whiteSpace: 'pre-wrap', fontSize: '0.85rem' }}>
                    {selectedMethod.comment}
                  </Typography>
                </Box>
              )}

              {/* Created Date */}
              <Box sx={{
                p: 1,
                bgcolor: '#f5f5f5',
                borderRadius: 1,
                borderLeft: '4px solid #ccc'
              }}>
                <Typography variant="caption" sx={{ color: '#999', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                  Created Date
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.2, color: '#666', fontSize: '0.85rem' }}>
                  {FORMAT_DATE(selectedMethod?.createDateTime)}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f9f9f9', borderTop: '1px solid #e0e0e0' }}>
          <Button
            onClick={() => setOpenViewDialog(false)}
            sx={{
              color: '#1a5f6b',
              fontWeight: 600,
              '&:hover': { bgcolor: '#f0f0f0' }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Delete Payment Method?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{methodToDelete?.name}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerPaymentMethods;
